import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { preferencesService } from '../services/preferencesService';
import type {
  UserPreferenceKey,
  UserPreferences,
} from '../types/preferences';
import type { SupportedUiLocale } from '../localization/locales';
import { useBrowserAuth } from './AuthContext';
import { useSession } from './SessionContext';

export type UserPreferencesErrorCode = (
  | 'preferences_load_failed'
  | 'preferences_save_failed'
  | 'preferences_session_unavailable'
);

export interface UserPreferencesErrorInfo {
  code: UserPreferencesErrorCode;
  detail: string | null;
}

export class UserPreferencesRequestError extends Error {
  readonly info: UserPreferencesErrorInfo;

  constructor(code: UserPreferencesErrorCode, detail: string | null = null) {
    super(detail || code);
    this.name = 'UserPreferencesRequestError';
    this.info = { code, detail };
  }
}

export const isUserPreferencesRequestError = (
  error: unknown,
): error is UserPreferencesRequestError => error instanceof UserPreferencesRequestError;

interface PreferenceLoadRequest {
  requestKey: string;
  promise: Promise<UserPreferences>;
}

export interface UserPreferencesContextValue {
  preferences: UserPreferences | null;
  isLoading: boolean;
  isInitialLoadComplete: boolean;
  loadError: UserPreferencesErrorInfo | null;
  loadPreferences: () => Promise<UserPreferences | null>;
  savePreference: (
    preferenceKey: UserPreferenceKey,
    value: SupportedUiLocale | null,
  ) => Promise<UserPreferences>;
}

const UserPreferencesContext = createContext<UserPreferencesContextValue | undefined>(undefined);

const createRequestKey = (userId: string | null, sessionId: string | null): string | null => (
  userId && sessionId ? JSON.stringify([userId, sessionId]) : null
);

const toErrorDetail = (error: unknown): string | null => (
  error instanceof Error && error.message ? error.message : null
);

export const UserPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const browserAuth = useBrowserAuth();
  const { session } = useSession();
  const authenticatedUserId = browserAuth.authenticated ? browserAuth.user?.id || null : null;
  const sessionId = session?.sessionId || null;
  const requestKey = createRequestKey(authenticatedUserId, sessionId);
  const [confirmedOwnerId, setConfirmedOwnerId] = useState<string | null>(null);
  const [confirmedPreferences, setConfirmedPreferences] = useState<UserPreferences | null>(null);
  const [loadingRequestKey, setLoadingRequestKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<{
    requestKey: string;
    error: UserPreferencesErrorInfo;
  } | null>(null);
  const [initialLoadCompletedRequestKey, setInitialLoadCompletedRequestKey] = useState<string | null>(null);
  const activeRequestKeyRef = useRef<string | null>(requestKey);
  const previousOwnerIdRef = useRef<string | null>(authenticatedUserId);
  const loadRequestRef = useRef<PreferenceLoadRequest | null>(null);

  activeRequestKeyRef.current = requestKey;

  const loadPreferences = useCallback(async (): Promise<UserPreferences | null> => {
    if (!requestKey || !authenticatedUserId || !sessionId) {
      return null;
    }

    const activeRequest = loadRequestRef.current;
    if (activeRequest?.requestKey === requestKey) {
      return activeRequest.promise;
    }

    setLoadingRequestKey(requestKey);
    setLoadError(null);

    const request = preferencesService.getPreferences(sessionId);
    loadRequestRef.current = { requestKey, promise: request };

    try {
      const preferences = await request;
      if (activeRequestKeyRef.current === requestKey) {
        setConfirmedOwnerId(authenticatedUserId);
        setConfirmedPreferences(preferences);
        setInitialLoadCompletedRequestKey(requestKey);
      }
      return preferences;
    } catch (error) {
      const errorInfo: UserPreferencesErrorInfo = {
        code: 'preferences_load_failed',
        detail: toErrorDetail(error),
      };
      if (activeRequestKeyRef.current === requestKey) {
        setLoadError({ requestKey, error: errorInfo });
        setInitialLoadCompletedRequestKey(requestKey);
      }
      throw new UserPreferencesRequestError(errorInfo.code, errorInfo.detail);
    } finally {
      if (loadRequestRef.current?.promise === request) {
        loadRequestRef.current = null;
      }
      if (activeRequestKeyRef.current === requestKey) {
        setLoadingRequestKey(null);
      }
    }
  }, [authenticatedUserId, requestKey, sessionId]);

  const savePreference = useCallback(async (
    preferenceKey: UserPreferenceKey,
    value: SupportedUiLocale | null,
  ): Promise<UserPreferences> => {
    const activeRequestKey = requestKey;
    if (!activeRequestKey || !authenticatedUserId || !sessionId) {
      throw new UserPreferencesRequestError('preferences_session_unavailable');
    }

    try {
      const savedPreferences = await preferencesService.updatePreferences(sessionId, {
        [preferenceKey]: value,
      });
      if (activeRequestKeyRef.current === activeRequestKey) {
        setConfirmedOwnerId(authenticatedUserId);
        setConfirmedPreferences(savedPreferences);
      }
      return savedPreferences;
    } catch (error) {
      throw new UserPreferencesRequestError('preferences_save_failed', toErrorDetail(error));
    }
  }, [authenticatedUserId, requestKey, sessionId]);

  useEffect(() => {
    const previousOwnerId = previousOwnerIdRef.current;
    previousOwnerIdRef.current = authenticatedUserId;

    if (!authenticatedUserId) {
      loadRequestRef.current = null;
      setConfirmedOwnerId(null);
      setConfirmedPreferences(null);
      setLoadingRequestKey(null);
      setLoadError(null);
      setInitialLoadCompletedRequestKey(null);
      return;
    }

    if (previousOwnerId !== authenticatedUserId) {
      setConfirmedOwnerId(null);
      setConfirmedPreferences(null);
      setLoadError(null);
      setInitialLoadCompletedRequestKey(null);
    }

    if (!requestKey) {
      return;
    }

    void loadPreferences().catch(() => undefined);
  }, [authenticatedUserId, loadPreferences, requestKey]);

  const preferences = confirmedOwnerId === authenticatedUserId
    ? confirmedPreferences
    : null;
  const isInitialLoadComplete = (
    !requestKey
    || preferences !== null
    || initialLoadCompletedRequestKey === requestKey
  );
  const visibleLoadError = loadError?.requestKey === requestKey ? loadError.error : null;

  return (
    <UserPreferencesContext.Provider value={{
      preferences,
      isLoading: loadingRequestKey === requestKey,
      isInitialLoadComplete,
      loadError: visibleLoadError,
      loadPreferences,
      savePreference,
    }}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

export const useUserPreferences = (): UserPreferencesContextValue => {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error('useUserPreferences must be used within UserPreferencesProvider');
  }
  return context;
};